import React from "react";
import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import {
  getAllDocsSnap,
  getAllDocs,
  uploadBatchDocs,
} from "../../Firebase/Firebase.utils";
import BgcBuildings from "../../data/BgcBuildings";
import BuildingCard from "../../OfficeLeasing/BuildingCard";
import BuildingListItem from "../../OfficeLeasing/BuildingListItem";
import DownloadExcel from "../../components/DownloadExcel";
import {
  FaSearch,
  FaFileExcel,
  FaBuilding,
  FaThumbsUp,
  FaRegThumbsDown,
  FaSlidersH,
  FaSort,
  FaEye,
  FaThList,
  FaTh,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";

function Masterlist() {
  const [buildings, setBuildings] = useState([]);
  const [activeMenu, setActiveMenu] = useState(0);
  const [activeList, setActiveList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState("grid"); // "grid" or "list"

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  useEffect(() => {
    const getBuildings = async () => {
      const bldg = await getAllDocsSnap("masterlist", (bldg) => {
        // arrange by buildingName
        bldg.sort((a, b) => (a.buildingName > b.buildingName ? 1 : -1));
        setBuildings(bldg);
        setActiveList(bldg);
        setLoading(false);
      });
    };
    getBuildings();
  }, []);

  const menuItems = [
    { name: "All Properties", icon: <FaBuilding />, count: buildings.length },
    {
      name: "Exclusive",
      icon: <FaThumbsUp />,
      count: buildings.filter((b) => b.exclusive === "Yes").length,
    },
    {
      name: "Non Exclusive",
      icon: <FaRegThumbsDown />,
      count: buildings.filter((b) => b.exclusive !== "Yes").length,
    },
  ];

  const activateMenu = (index) => {
    setActiveMenu(index);
    setCurrentPage(1); // Reset to first page when changing filters
    if (index === 0) {
      setActiveList(buildings);
    } else if (index === 1) {
      const filtered = buildings.filter(
        (building) => building.exclusive === "Yes"
      );
      setActiveList(filtered);
    } else {
      const filtered = buildings.filter(
        (building) => building.exclusive !== "Yes"
      );
      setActiveList(filtered);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Filtered buildings based on search
  const filteredBuildings = activeList.filter((building) => {
    if (building.buildingName) {
      return building.buildingName
        .toString()
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
    } else {
      return false;
    }
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredBuildings.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredBuildings.slice(
    indexOfFirstItem,
    indexOfLastItem
  );

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    // Scroll to top of the component
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pageNumbers = [];
    const maxPageButtons = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPageButtons / 2));
    let endPage = Math.min(totalPages, startPage + maxPageButtons - 1);

    if (endPage - startPage + 1 < maxPageButtons) {
      startPage = Math.max(1, endPage - maxPageButtons + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return (
      <div className="flex items-center justify-center mt-8 gap-2">
        <button
          onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={`flex items-center justify-center w-9 h-9 rounded-md ${
            currentPage === 1
              ? "text-gray-400 cursor-not-allowed bg-gray-100"
              : "text-gray-700 hover:bg-[#002368] hover:text-white border border-gray-200"
          }`}
        >
          <FaChevronLeft size={14} />
        </button>

        {startPage > 1 && (
          <>
            <button
              onClick={() => handlePageChange(1)}
              className={`flex items-center justify-center w-9 h-9 rounded-md border border-gray-200 hover:bg-[#002368] hover:text-white ${
                currentPage === 1 ? "bg-[#002368] text-white" : "text-gray-700"
              }`}
            >
              1
            </button>
            {startPage > 2 && <span className="px-1">...</span>}
          </>
        )}

        {pageNumbers.map((number) => (
          <button
            key={number}
            onClick={() => handlePageChange(number)}
            className={`flex items-center justify-center w-9 h-9 rounded-md ${
              currentPage === number
                ? "bg-[#002368] text-white"
                : "text-gray-700 border border-gray-200 hover:bg-[#002368] hover:text-white"
            }`}
          >
            {number}
          </button>
        ))}

        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && <span className="px-1">...</span>}
            <button
              onClick={() => handlePageChange(totalPages)}
              className={`flex items-center justify-center w-9 h-9 rounded-md border border-gray-200 hover:bg-[#002368] hover:text-white ${
                currentPage === totalPages
                  ? "bg-[#002368] text-white"
                  : "text-gray-700"
              }`}
            >
              {totalPages}
            </button>
          </>
        )}

        <button
          onClick={() =>
            currentPage < totalPages && handlePageChange(currentPage + 1)
          }
          disabled={currentPage === totalPages}
          className={`flex items-center justify-center w-9 h-9 rounded-md ${
            currentPage === totalPages
              ? "text-gray-400 cursor-not-allowed bg-gray-100"
              : "text-gray-700 hover:bg-[#002368] hover:text-white border border-gray-200"
          }`}
        >
          <FaChevronRight size={14} />
        </button>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 p-6">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-300"></div>
        <p className="mt-4 text-gray-600">Loading properties...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {/* Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
        {menuItems.map((item, index) => (
          <div
            key={index}
            onClick={() => activateMenu(index)}
            className={`
              flex items-center justify-between p-4 rounded-lg cursor-pointer border transition-all duration-300
              ${
                activeMenu === index
                  ? "bg-white border-[#ff5003] shadow-sm"
                  : "bg-white border-gray-100 hover:border-gray-300"
              }
            `}
          >
            <div className="flex items-center gap-3">
              <div
                className={`
                p-3 rounded-lg 
                ${
                  activeMenu === index
                    ? "bg-[#ff5003]/10 text-[#ff5003]"
                    : "bg-gray-50 text-gray-500"
                }
              `}
              >
                {item.icon}
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-600">
                  {item.name}
                </h3>
                <p
                  className={`text-2xl font-bold ${
                    activeMenu === index ? "text-[#002368]" : "text-gray-700"
                  }`}
                >
                  {item.count}
                </p>
              </div>
            </div>
            <div
              className={`
              rounded-full p-2 
              ${activeMenu === index ? "text-[#ff5003]" : "text-gray-400"}
            `}
            >
              <FaEye />
            </div>
          </div>
        ))}
      </div>

      {/* Search and Controls */}
      <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-8">
        <div className="relative w-full md:w-2/3">
          <input
            type="search"
            placeholder="Search buildings by name..."
            className="border border-gray-200 p-3 pl-12 rounded-lg w-full focus:outline-none focus:ring-1 focus:ring-gray-300 bg-white"
            onChange={handleSearch}
            value={searchTerm}
          />
          <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
        </div>

        <div className="flex items-center gap-3 w-full md:w-auto">
          {/* View Toggle */}
          <div className="flex border border-gray-200 rounded-lg overflow-hidden">
            <button
              onClick={() => setViewMode("grid")}
              className={`flex items-center gap-1 py-2.5 px-3 ${
                viewMode === "grid"
                  ? "bg-[#002368] text-white"
                  : "bg-white text-gray-700 hover:bg-gray-50"
              }`}
            >
              <FaTh size={14} />
            </button>
            <button
              onClick={() => setViewMode("list")}
              className={`flex items-center gap-1 py-2.5 px-3 ${
                viewMode === "list"
                  ? "bg-[#002368] text-white"
                  : "bg-white text-gray-700 hover:bg-gray-50"
              }`}
            >
              <FaThList size={14} />
            </button>
          </div>

          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 bg-white hover:bg-gray-50 border border-gray-200 text-gray-700 py-2.5 px-4 rounded-lg transition-all duration-200"
          >
            <FaSlidersH /> Filters
          </button>

          <button
            onClick={() => {
              document.getElementById("excelDownload").click();
            }}
            className="flex items-center gap-2 bg-white hover:bg-gray-50 border border-gray-200 text-gray-700 py-2.5 px-4 rounded-lg transition-all duration-200"
          >
            <FaFileExcel className="text-[#ff5003]" /> Export
          </button>
          <div className="hidden">
            <DownloadExcel
              id="excelDownload"
              data={filteredBuildings}
              label={"Download Excel"}
              filename={`Masterlist ${new Date().toLocaleString()}`}
            />
          </div>
        </div>
      </div>

      {/* Optional Filters Panel */}
      {showFilters && (
        <div className="bg-white rounded-lg border border-gray-100 p-4 mb-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Business District
            </label>
            <select className="w-full border border-gray-200 rounded-lg p-2 focus:outline-none focus:ring-1 focus:ring-gray-300">
              <option value="">All Districts</option>
              <option value="BGC">BGC</option>
              <option value="Makati">Makati</option>
              <option value="Ortigas">Ortigas</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Developer
            </label>
            <select className="w-full border border-gray-200 rounded-lg p-2 focus:outline-none focus:ring-1 focus:ring-gray-300">
              <option value="">All Developers</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sort By
            </label>
            <div className="flex items-center gap-2">
              <select className="w-full border border-gray-200 rounded-lg p-2 focus:outline-none focus:ring-1 focus:ring-gray-300">
                <option value="name">Building Name</option>
                <option value="date">Date Added</option>
              </select>
              <button className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50">
                <FaSort />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Results Count and Page Info */}
      <div className="flex items-center justify-between mb-6">
        <div className="text-gray-700 font-medium">
          Showing {indexOfFirstItem + 1}-
          {Math.min(indexOfLastItem, filteredBuildings.length)} of{" "}
          {filteredBuildings.length} properties
        </div>
        {filteredBuildings.length > 0 && searchTerm && (
          <div className="text-sm text-gray-500">
            Search results for:{" "}
            <span className="font-medium">{searchTerm}</span>
          </div>
        )}
      </div>

      {/* Building Cards/List */}
      {filteredBuildings.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-100 p-8 text-center">
          <FaBuilding className="text-gray-200 text-5xl mx-auto mb-4" />
          <h3 className="text-xl font-medium text-gray-700 mb-2">
            No properties found
          </h3>
          <p className="text-gray-500">
            Try adjusting your search or filters to find what you're looking
            for.
          </p>
        </div>
      ) : (
        <>
          {/* Grid View */}
          {viewMode === "grid" && (
            <div className="flex items-center justify-center gap-6 flex-wrap">
              {currentItems.map((building, index) => (
                <BuildingCard key={index} building={building} />
              ))}
            </div>
          )}

          {/* List View */}
          {viewMode === "list" && (
            <div className="flex flex-col gap-4">
              {currentItems.map((building, index) => (
                <BuildingListItem key={index} building={building} />
              ))}
            </div>
          )}
        </>
      )}

      {/* Pagination */}
      {renderPagination()}
    </div>
  );
}

export default Masterlist;
